export const COMPARISONS = [
  {
    slug: "invideo-ai",
    name: "InVideo AI",
    metaTitle: "Videoileo vs InVideo AI (2026): Which Is Better?",
    description:
      "Honest 2026 comparison of Videoileo and InVideo AI for faceless YouTube channels: scripts, voiceovers, visuals, captions, scheduling, autopilot and pricing.",
    tagline: "Full production studio vs. hands-off faceless channel engine",
    intro: [
      "InVideo AI is a broad video production studio: a timeline editor, a large stock library and generative visual models. It shines when you want granular creative control and don't mind assembling videos yourself.",
      "Videoileo is built for one job: growing a faceless channel on autopilot. Type a topic and it writes the script, records the voiceover, generates original visuals, burns in speech-synced captions, renders the MP4 and can schedule a whole series straight to YouTube.",
    ],
    rows: [
      { feature: "AI script writing", us: "Yes — hook-driven, retention-optimized", them: "Yes — prompt-based" },
      { feature: "Studio AI voiceovers", us: "Yes + 5 AI narrator personas", them: "Yes, incl. voice cloning" },
      { feature: "On-screen animated narrator", us: true, them: false },
      { feature: "Original AI visuals (cinematic, comic, anime)", us: true, them: "Yes — generative models + stock" },
      { feature: "AI 3D motion on scenes", us: true, them: "Partial (generative clips)" },
      { feature: "Speech-synced burned-in captions", us: "Yes — styles, positions, karaoke", them: "Yes" },
      { feature: "Long-form videos (5–8 min)", us: true, them: true },
      { feature: "Vertical 9:16 shorts", us: true, them: true },
      { feature: "Series autopilot (auto-create + auto-post to YouTube)", us: true, them: false },
      { feature: "Manual timeline editing", us: "Scene-level editing", them: "Yes — full timeline" },
      { feature: "Free to start", us: "100 free credits, no card", them: "Free tier (watermarked)" },
      { feature: "Starting price", us: "$19/mo", them: "~$25/mo" },
    ],
    chooseThem: [
      "You want a traditional timeline editor with granular, clip-by-clip control",
      "You need a massive stock footage library for realistic b-roll",
      "You make many kinds of videos, not just faceless channel content",
    ],
    chooseUs: [
      "You want a faceless channel that runs itself — script to published video",
      "You want a consistent AI narrator persona with an on-screen animated avatar",
      "You'd rather schedule a whole series to YouTube than edit one video at a time",
    ],
    faqs: [
      { q: "Is InVideo AI or Videoileo better for YouTube automation?", a: "InVideo AI is a stronger manual editor; Videoileo is stronger at automation. Videoileo's series autopilot writes, renders and posts episodes to YouTube on a schedule — InVideo requires you to create and upload each video yourself." },
      { q: "Which is cheaper?", a: "Videoileo plans start at $19/month and you can start free with 100 credits and no card. InVideo AI typically starts around $25/month with a watermarked free tier." },
      { q: "Can I try Videoileo before paying?", a: "Yes — sign up with just an email and get 100 free credits, enough for a couple of full videos end-to-end. No card required." },
    ],
  },
  {
    slug: "pictory",
    name: "Pictory",
    metaTitle: "Videoileo vs Pictory (2026): Which Is Better?",
    description:
      "Honest 2026 comparison of Videoileo and Pictory for faceless video creators: original AI visuals vs stock repurposing, captions, voiceovers, autopilot and pricing.",
    tagline: "Content repurposer vs. original faceless video creator",
    intro: [
      "Pictory is excellent at repurposing: paste a blog post or upload a long video and it matches stock footage to your text and cuts polished clips. Marketers and bloggers love it for exactly that.",
      "Videoileo creates original content from scratch. It writes the script for a topic you choose, generates original AI visuals in cinematic, comic-book or anime styles, records the voiceover and publishes on a schedule — no source article or footage needed.",
    ],
    rows: [
      { feature: "AI script writing from a topic", us: "Yes — hook-driven, retention-optimized", them: "Limited (works best from existing text)" },
      { feature: "Repurpose blogs / long videos into clips", us: false, them: "Yes — its specialty" },
      { feature: "Original AI visuals (cinematic, comic, anime)", us: true, them: "No — stock footage matching" },
      { feature: "Studio AI voiceovers", us: "Yes + 5 AI narrator personas", them: "Yes (ElevenLabs on higher tiers)" },
      { feature: "On-screen animated narrator", us: true, them: false },
      { feature: "Speech-synced burned-in captions", us: "Yes — styles, positions, karaoke", them: "Yes — strong caption editor" },
      { feature: "AI 3D motion on scenes", us: true, them: false },
      { feature: "Long-form videos (5–8 min)", us: true, them: true },
      { feature: "Series autopilot (auto-create + auto-post to YouTube)", us: true, them: false },
      { feature: "Free to start", us: "100 free credits, no card", them: "Free trial" },
      { feature: "Starting price", us: "$19/mo", them: "~$25–29/mo" },
    ],
    chooseThem: [
      "You already have blogs, webinars or podcasts you want to turn into clips",
      "You prefer realistic stock footage over AI-generated visuals",
      "Caption fine-tuning on existing footage is your main job",
    ],
    chooseUs: [
      "You're starting from a topic, not existing content — Videoileo writes the script for you",
      "You want original visuals in a consistent style instead of recycled stock clips",
      "You want episodes created and posted to YouTube automatically, week after week",
    ],
    faqs: [
      { q: "Is Pictory or Videoileo better for faceless YouTube channels?", a: "If you're repurposing existing articles or recordings, Pictory. If you're building a faceless channel from scratch — original scripts, visuals, voiceovers and scheduled publishing — Videoileo is built for exactly that." },
      { q: "Does Videoileo use stock footage like Pictory?", a: "No. Videoileo generates original AI visuals per scene in styles like cinematic, comic book and anime, and can add true 3D motion — so your channel doesn't look like everyone else's stock clips." },
      { q: "Can I try Videoileo free?", a: "Yes — 100 free credits on signup with no card, enough for a couple of complete videos." },
    ],
  },
  {
    slug: "fliki",
    name: "Fliki",
    metaTitle: "Videoileo vs Fliki (2026): Which Is Better?",
    description:
      "Honest 2026 comparison of Videoileo and Fliki: voices, original AI visuals vs stock, captions, long-form, series scheduling to YouTube and pricing.",
    tagline: "Voice-first volume tool vs. complete channel studio",
    intro: [
      "Fliki is a voice-first generator with a huge library of voices across 80+ languages. It's fast, affordable and great for churning out narrated explainers on stock visuals.",
      "Videoileo goes further down the channel-building path: original AI visuals instead of stock, speech-synced captions, an animated on-screen narrator, long-form episodes and a series autopilot that schedules straight to YouTube.",
    ],
    rows: [
      { feature: "AI script writing", us: "Yes — hook-driven, retention-optimized", them: "Basic" },
      { feature: "Voice library", us: "Studio AI voices + 5 narrator personas + ElevenLabs", them: "2,000+ voices, 80+ languages" },
      { feature: "Original AI visuals (cinematic, comic, anime)", us: true, them: "No — mostly stock media" },
      { feature: "On-screen animated narrator", us: true, them: false },
      { feature: "Speech-synced burned-in captions", us: "Yes — styles, positions, karaoke", them: "Yes — basic" },
      { feature: "AI 3D motion on scenes", us: true, them: false },
      { feature: "Long-form videos (5–8 min)", us: true, them: "Yes (tier-limited minutes)" },
      { feature: "Vertical 9:16 shorts", us: true, them: true },
      { feature: "Series autopilot (auto-create + auto-post to YouTube)", us: true, them: false },
      { feature: "Free to start", us: "100 free credits, no card", them: "Free tier (limited minutes)" },
      { feature: "Starting price", us: "$19/mo", them: "~$8/mo (annual, entry tier)" },
    ],
    chooseThem: [
      "You need narration in many languages at the lowest possible price",
      "Stock footage is fine for your niche and you value speed over originality",
      "You mostly make short narrated explainers, not a growing channel",
    ],
    chooseUs: [
      "You want your channel to look original, not assembled from the same stock clips",
      "You want captions that land exactly on the spoken words, in your style",
      "You want a series that writes, renders and posts itself to YouTube",
    ],
    faqs: [
      { q: "Is Fliki cheaper than Videoileo?", a: "Fliki's entry tier is cheaper (~$8/month annual), but it's voice-minutes focused with stock visuals. Videoileo starts at $19/month and includes original AI visuals, synced captions, long-form rendering and YouTube series scheduling." },
      { q: "Which has better voices?", a: "Fliki has the bigger multilingual library. Videoileo focuses on channel consistency: studio-grade AI voices plus 5 narrator personas with on-screen animated avatars, and optional ElevenLabs voices." },
      { q: "Can Videoileo publish to YouTube automatically?", a: "Yes — series autopilot creates episodes on a cadence you set and schedules them straight to your YouTube channel." },
    ],
  },
  {
    slug: "autoshorts-ai",
    name: "AutoShorts.ai",
    metaTitle: "Videoileo vs AutoShorts.ai (2026): Which Is Better?",
    description:
      "Honest 2026 comparison of Videoileo and AutoShorts.ai: shorts autopilot vs full channel studio, long-form, editing control, captions and pricing.",
    tagline: "Set-and-forget shorts vs. autopilot with creative control",
    intro: [
      "AutoShorts.ai does one thing: fully automated short videos posted to YouTube and TikTok on a schedule. It's genuinely hands-off — and intentionally generic, since you trade away editorial control for volume.",
      "Videoileo gives you the same autopilot (series that write, render and post themselves) plus the controls that make a channel worth subscribing to: editable scripts and scenes, original visual styles, narrator personas, synced caption styles and real long-form episodes.",
    ],
    rows: [
      { feature: "Fully automated posting schedule", us: "Yes — series autopilot to YouTube", them: "Yes — YouTube & TikTok" },
      { feature: "Long-form videos (5–8 min)", us: true, them: false },
      { feature: "Edit scripts & scenes before publishing", us: true, them: "Very limited" },
      { feature: "Original AI visuals (cinematic, comic, anime)", us: true, them: "Template-style AI visuals" },
      { feature: "On-screen animated narrator", us: true, them: false },
      { feature: "Speech-synced captions with styles & positions", us: true, them: "Basic captions" },
      { feature: "AI 3D motion on scenes", us: true, them: false },
      { feature: "Voice choice", us: "Studio AI voices + 5 narrator personas + ElevenLabs", them: "Limited set" },
      { feature: "Free to start", us: "100 free credits, no card", them: "Limited trial" },
      { feature: "Starting price", us: "$19/mo", them: "$19/mo (3 posts/week)" },
    ],
    chooseThem: [
      "You want zero involvement and TikTok auto-posting is a must today",
      "You're testing many niches with volume and don't care about editorial quality",
      "Short-form only is fine for your strategy",
    ],
    chooseUs: [
      "You want autopilot AND the ability to shape scripts, visuals and captions",
      "You want long-form episodes — where YouTube watch time and ad revenue live",
      "You want a channel identity: consistent narrator, style and caption look",
    ],
    faqs: [
      { q: "Is AutoShorts.ai or Videoileo more automated?", a: "Both run on autopilot. AutoShorts.ai posts shorts to YouTube and TikTok hands-off; Videoileo's series autopilot writes, renders and schedules episodes to YouTube — and unlike AutoShorts, you can step in and edit any episode before it goes out." },
      { q: "Can Videoileo make long-form videos?", a: "Yes — 5–8 minute long-form episodes plus vertical 9:16 cuts. AutoShorts.ai is short-form only." },
      { q: "Which is better for growing subscribers?", a: "Volume alone rarely grows a channel. Videoileo pairs automation with retention-focused scripts, original visuals and a consistent narrator persona — the things that make viewers subscribe." },
    ],
  },
];

export const getComparison = (slug) => COMPARISONS.find((c) => c.slug === slug);
